import { supabase } from "@/integrations/supabase/client";

/**
 * Log user activity to the activity_logs table
 * @param userId The ID of the user performing the action
 * @param action The action being performed (e.g., 'login', 'create_project')
 * @param metadata Optional metadata about the action (as a JSON object)
 * @returns Promise that resolves when the log is created
 */
export const logActivity = async (
  userId: string,
  action: string,
  metadata: Record<string, any> = {}
): Promise<void> => {
  try {
    await supabase.from('activity_logs').insert({
      user_id: userId,
      action,
      metadata,
      created_at: new Date().toISOString()
    });
  } catch (error) {
    
    // Silent failure - don't interrupt user experience if logging fails
  }
};

/**
 * List of standard action types for consistent logging
 */
export const ActivityActions = {
  // Authentication
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGNUP: 'signup',
  RESET_PASSWORD: 'reset_password',
  
  // Projects
  CREATE_PROJECT: 'create_project',
  UPDATE_PROJECT: 'update_project',
  DELETE_PROJECT: 'delete_project',
  
  // Project Sharing & Members
  CREATE_SHARE_CODE: 'create_share_code',
  ACCEPT_SHARE_CODE: 'accept_share_code',
  REVOKE_SHARE_CODE: 'revoke_share_code',
  ADD_PROJECT_MEMBER: 'add_project_member',
  REMOVE_PROJECT_MEMBER: 'remove_project_member',
  
  // Reports
  CREATE_REPORT: 'create_report',
  UPDATE_REPORT: 'update_report',
  DELETE_REPORT: 'delete_report',
  
  // Entries
  CREATE_ENTRY: 'create_entry',
  UPDATE_ENTRY: 'update_entry',
  DELETE_ENTRY: 'delete_entry',
  
  // Payments
  CREATE_PAYMENT: 'create_payment',
  UPDATE_PAYMENT: 'update_payment',
  DELETE_PAYMENT: 'delete_payment',
  
  // Admin actions
  REDEEM_CODE: 'redeem_code',
  SEND_NOTIFICATION: 'send_push_notification',
  UPDATE_ADMIN_STATUS: 'update_admin_status',
  
  // User profile
  UPDATE_PROFILE: 'update_profile',
  
  // Files and photos
  UPLOAD_FILE: 'upload_file',
  DELETE_FILE: 'delete_file',
  
  // Account management
  DELETE_ACCOUNT: 'delete_account',

  // User Interaction Tracking (New)
  USER_NAVIGATION: 'user_navigation',
  USER_CLICK: 'user_click',
  USER_FORM_INTERACTION: 'user_form_interaction',
  USER_SCROLL: 'user_scroll',
  USER_FOCUS: 'user_focus',
  USER_IDLE: 'user_idle',
  USER_CUSTOM: 'user_custom',

  // App Events
  APP_ERROR: 'app_error',
  APP_PERFORMANCE: 'app_performance',
  
  // Feature Usage
  FEATURE_USED: 'feature_used',
  SEARCH_PERFORMED: 'search_performed',
  FILTER_APPLIED: 'filter_applied',
  EXPORT_DATA: 'export_data',
  IMPORT_DATA: 'import_data',
};

/**
 * Enhanced activity logging with automatic metadata enrichment
 */
export const logEnhancedActivity = async (
  userId: string,
  action: string,
  metadata: Record<string, any> = {},
  options: {
    includeUserAgent?: boolean;
    includeLocation?: boolean;
    includePerformance?: boolean;
  } = {}
): Promise<void> => {
  try {
    const enhancedMetadata = { ...metadata };

    // Add user agent if requested
    if (options.includeUserAgent) {
      enhancedMetadata.userAgent = navigator.userAgent;
      enhancedMetadata.platform = navigator.platform;
      enhancedMetadata.language = navigator.language;
    }

    // Add location info if requested and available
    if (options.includeLocation && window.location) {
      enhancedMetadata.location = {
        pathname: window.location.pathname,
        search: window.location.search,
        hash: window.location.hash,
        referrer: document.referrer
      };
    }

    // Add performance metrics if requested
    if (options.includePerformance && 'performance' in window) {
      const navigation = performance.getEntriesByType('navigation')[0] as any;
      if (navigation) {
        enhancedMetadata.performance = {
          loadTime: navigation.loadEventEnd - navigation.loadEventStart,
          domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
          connectionType: (navigator as any).connection?.effectiveType || 'unknown'
        };
      }
    }

    await logActivity(userId, action, enhancedMetadata);
  } catch (error) {
    // Silent failure
  }
};

/**
 * Track user session activity
 */
export const logSessionActivity = async (
  userId: string,
  sessionData: {
    sessionStart?: Date;
    sessionEnd?: Date;
    pagesVisited?: string[];
    actionsPerformed?: number;
    totalTimeSpent?: number;
  }
): Promise<void> => {
  try {
    await logActivity(userId, 'session_activity', {
      ...sessionData,
      sessionStart: sessionData.sessionStart?.toISOString(),
      sessionEnd: sessionData.sessionEnd?.toISOString(),
    });
  } catch (error) {
    // Silent failure
  }
};

/**
 * Track feature usage with context
 */
export const logFeatureUsage = async (
  userId: string,
  featureName: string,
  context: Record<string, any> = {}
): Promise<void> => {
  try {
    await logActivity(userId, ActivityActions.FEATURE_USED, {
      feature: featureName,
      context,
      timestamp: new Date().toISOString(),
    });
  } catch (error) {
    // Silent failure
  }
};

/**
 * Track errors with context
 */
export const logError = async (
  userId: string | null,
  error: Error | string,
  context: Record<string, any> = {}
): Promise<void> => {
  try {
    const errorData = {
      error: typeof error === 'string' ? error : error.message,
      stack: typeof error === 'object' ? error.stack : undefined,
      context,
      timestamp: new Date().toISOString(),
      page: window.location.pathname,
    };

    if (userId) {
      await logActivity(userId, ActivityActions.APP_ERROR, errorData);
    }
  } catch (logError) {
    // Silent failure
  }
}; 